import React from 'react';
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';

const ModernPagination = ({
  currentPage = 0,
  totalPages = 0,
  onPageChange,
  showFirstLast = true,
  maxVisiblePages = 5,
  className = ''
}) => {
  // Don't render if there's only one page or no pages
  if (totalPages <= 1) {
    return null;
  }

  const handlePageClick = (page) => {
    if (page >= 0 && page < totalPages && page !== currentPage) {
      onPageChange(page);
    }
  };

  const getVisiblePages = () => {
    const pages = [];
    const halfVisible = Math.floor(maxVisiblePages / 2);
    
    let startPage = Math.max(0, currentPage - halfVisible);
    let endPage = Math.min(totalPages - 1, currentPage + halfVisible);

    // Adjust if we're near the beginning or end
    if (endPage - startPage + 1 < maxVisiblePages) {
      if (startPage === 0) {
        endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 1);
      } else {
        startPage = Math.max(0, endPage - maxVisiblePages + 1);
      }
    }

    // Add first page and ellipsis if needed
    if (startPage > 0) {
      pages.push(0);
      if (startPage > 1) {
        pages.push('ellipsis-start');
      }
    }

    // Add visible pages
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    // Add ellipsis and last page if needed
    if (endPage < totalPages - 1) {
      if (endPage < totalPages - 2) {
        pages.push('ellipsis-end');
      }
      pages.push(totalPages - 1);
    }

    return pages;
  };

  const visiblePages = getVisiblePages();

  return (
    <div className={`flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-2 py-6 ${className}`}>
      {/* Mobile: Simplified Navigation */}
      <div className="flex items-center space-x-2 sm:hidden">
        <button
          onClick={() => handlePageClick(currentPage - 1)}
          disabled={currentPage === 0}
          className={`flex items-center justify-center w-12 h-12 rounded-xl border transition-all duration-300 transform ${
            currentPage === 0
              ? 'bg-gray-500/20 border-gray-500/30 text-gray-400 cursor-not-allowed'
              : 'bg-white/10 border-white/20 text-white hover:bg-white/20 hover:border-white/30 hover:scale-105 backdrop-blur-sm'
          }`}
        >
          <ChevronLeft size={24} />
        </button>

        <div className="px-4 py-2 rounded-xl bg-white/10 border border-white/20 backdrop-blur-sm">
          <span className="text-white font-bold">
            {currentPage + 1} / {totalPages}
          </span>
        </div>

        <button
          onClick={() => handlePageClick(currentPage + 1)}
          disabled={currentPage === totalPages - 1}
          className={`flex items-center justify-center w-12 h-12 rounded-xl border transition-all duration-300 transform ${
            currentPage === totalPages - 1
              ? 'bg-gray-500/20 border-gray-500/30 text-gray-400 cursor-not-allowed'
              : 'bg-white/10 border-white/20 text-white hover:bg-white/20 hover:border-white/30 hover:scale-105 backdrop-blur-sm'
          }`}
        >
          <ChevronRight size={24} />
        </button>
      </div>

      {/* Desktop: Full Navigation */}
      <div className="hidden sm:flex items-center space-x-2">
        {/* First Page Button */}
        {showFirstLast && currentPage > 0 && (
          <button
            onClick={() => handlePageClick(0)}
            className="flex items-center justify-center w-10 h-10 rounded-xl bg-white/10 border border-white/20 text-white hover:bg-white/20 hover:border-white/30 transition-all duration-300 transform hover:scale-105 backdrop-blur-sm"
            title="First page"
          >
            <ChevronLeft size={16} />
            <ChevronLeft size={16} className="-ml-1" />
          </button>
        )}

        {/* Previous Button */}
        <button
          onClick={() => handlePageClick(currentPage - 1)}
          disabled={currentPage === 0}
          className={`flex items-center justify-center w-10 h-10 rounded-xl border transition-all duration-300 transform ${
            currentPage === 0
              ? 'bg-gray-500/20 border-gray-500/30 text-gray-400 cursor-not-allowed'
              : 'bg-white/10 border-white/20 text-white hover:bg-white/20 hover:border-white/30 hover:scale-105 backdrop-blur-sm'
          }`}
          title="Previous page"
        >
          <ChevronLeft size={20} />
        </button>

        {/* Page Numbers */}
        <div className="flex items-center space-x-1">
          {visiblePages.map((page, index) => {
            if (page === 'ellipsis-start' || page === 'ellipsis-end') {
              return (
                <div
                  key={`ellipsis-${index}`}
                  className="flex items-center justify-center w-10 h-10 text-white/60"
                >
                  <MoreHorizontal size={20} />
                </div>
              );
            }

            const isActive = page === currentPage;
            
            return (
              <button
                key={page}
                onClick={() => handlePageClick(page)}
                className={`flex items-center justify-center w-10 h-10 rounded-xl font-bold transition-all duration-300 transform ${
                  isActive
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 border border-blue-400/50 text-white shadow-lg shadow-blue-500/25 scale-110'
                    : 'bg-white/10 border border-white/20 text-white hover:bg-white/20 hover:border-white/30 hover:scale-105 backdrop-blur-sm'
                }`}
              >
                {page + 1}
              </button>
            );
          })}
        </div>

        {/* Next Button */}
        <button
          onClick={() => handlePageClick(currentPage + 1)}
          disabled={currentPage === totalPages - 1}
          className={`flex items-center justify-center w-10 h-10 rounded-xl border transition-all duration-300 transform ${
            currentPage === totalPages - 1
              ? 'bg-gray-500/20 border-gray-500/30 text-gray-400 cursor-not-allowed'
              : 'bg-white/10 border-white/20 text-white hover:bg-white/20 hover:border-white/30 hover:scale-105 backdrop-blur-sm'
          }`}
          title="Next page"
        >
          <ChevronRight size={20} />
        </button>

        {/* Last Page Button */}
        {showFirstLast && currentPage < totalPages - 1 && (
          <button
            onClick={() => handlePageClick(totalPages - 1)}
            className="flex items-center justify-center w-10 h-10 rounded-xl bg-white/10 border border-white/20 text-white hover:bg-white/20 hover:border-white/30 transition-all duration-300 transform hover:scale-105 backdrop-blur-sm"
            title="Last page"
          >
            <ChevronRight size={16} />
            <ChevronRight size={16} className="-ml-1" />
          </button>
        )}
      </div>

      {/* Page Info - Desktop Only */}
      <div className="hidden sm:block ml-4 px-4 py-2 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm">
        <span className="text-white/80 text-sm">
          Page {currentPage + 1} of {totalPages}
        </span>
      </div>
    </div>
  );
};

export default ModernPagination;
